import Immutable from 'immutable';

/**
 * Gets the value at path of object. If the path does not exist, or the resolved value is undefined,
 * the defaultValue is returned. Optionally run a function over the data before returning it.
 * @param {Object|string|string[]|Immutable.Map|Immutable.List} object - The object to query.
 * @param {String} path - The path of the property to get (dot separated).
 * @param {*} [defaultValue] - The value returned if path doesn't exist or undefined is resolved.
 * @param {Function} transform - Optional transform to run on resolved data.
 * @returns {*} Returns the resolved value.
 */
export const get = (object, path, defaultValue, transform = (x) => x) => {
    const separator = '.';
    const leadingSeparatorRe = /^\.+/;

    try {
        // Find the value in "object" using "path"
        const resolvedValue = path
            // convert brackets into "separator" (array properties can be accessed just like a plain object)
            .replace(/[[]/g, separator)
            .replace(/]/g, '')

            // strip leading "separator" (this occurs when using array keys only)
            .replace(leadingSeparatorRe, '')

            // separate the path into parts
            .split(separator)

            // iterate over the pathParts until the resolved value is returned
            .reduce((data, pathPart) => {
                if (Immutable.Map.isMap(data) || Immutable.List.isList(data)) {
                    return data.get(pathPart);
                }
                return data[pathPart];
            }, object);

        // If undefined, return the defaultValue
        if (typeof resolvedValue === 'undefined') {
            return defaultValue;
        }
        return transform(resolvedValue);
    } catch (error) {
        // Nom, nom, nom (eating any errors thrown)
        // If the path cannot be resolved, return the defaultValue (which might be "undefined" if not specified)
        // It may or may not be an error if the path cannot be resolved,
        // if you want to know why it cannot be resolved add a console.log() below :)
    }

    return defaultValue;
};

/**
 * Shorthand for getting stuff out of state formatted the way martech needs.
 * @param {*} obj       - The object to query.
 * @param {string} path - The path of the property to get.
 * @returns {*}         - The resolved value.
 */
export const getMartech = (obj) => {
    const formatData = (value) => {
        if (typeof value !== 'string') return value;
        const dataMap = {
            y: true,
            yes: true,
            n: false,
            no: false
        };
        const key = value.toLowerCase();
        if (dataMap[key] !== undefined) return dataMap[key];
        return value;
    };
    return (path) => get(obj, path, null, formatData);
};
